/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { ElasticFilterInput } from "types/gql-types";

type RangeData = string | number;
type ArrayFilterSetting = (string | number | boolean)[];
export type RangeFilterSetting = { min?: RangeData; max?: RangeData };
type FilterSetting = string | number | boolean | RangeFilterSetting | ArrayFilterSetting;
type FilterType = "text" | "number" | "boolean" | "range" | "rangeMin" | "rangeMax" | "array" | "inverseBoolean";

export type FilterData = {
  type: "text" | "number" | "boolean" | "range" | "array" | "inverseBoolean";
  value?: FilterSetting;
  values?: ArrayFilterSetting;
  min?: RangeData;
  max?: RangeData;
}

type FilterRecordType = Record<string, FilterData>

type PageState = {
  page: number;
  nbPages: number;
  pageSize: number;
};

type SearchState = {
  page: number;
  nbHits: number;
  hits: any[];
};
type FilterManager = {
  setTextFilter: (query: string) => void;
  addFilter: (filterName: string, value: FilterSetting, filterType?: FilterType) => void;
  removeFilter: (filterName: string, filterType?: FilterType) => void;
  clearFilters: () => void;
  pageState: PageState;
  setNbPages: (nbPages: number) => void;
  setSelectedPage: (page: number) => void;
  filterState: FilterRecordType;
  setFilterState: React.Dispatch<React.SetStateAction<FilterRecordType>>;
  searchState: SearchState,
  setSearchState: React.Dispatch<React.SetStateAction<SearchState>>;
  search: () => void;
  interestListIds: string[];
  setInterestListIds: (ids: string[]) => void;
  textFilter: string;
  searchFlag: boolean;
  setSearchFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarFiltersContext = React.createContext<FilterManager | null>(null);

export const formatFilters = (input: FilterRecordType): ElasticFilterInput[] =>
  Object.entries(input).map(([field, { type, value }]) => {
    const output = { field, type } as ElasticFilterInput;
    if (type === "array") {
      output.values = value as string[];
    } else if (type === "range" && value) {
      const rangeFilter = value as RangeFilterSetting;
      output.min = rangeFilter.min ? `${rangeFilter.min}` : null;
      output.max = rangeFilter.max ? `${rangeFilter.max}` : null;
    } else {
      output.value = value ? `${value}` : null;
    }
    return output;
  });

const defineFilters = (): FilterManager => {
  const [interestListIds, setInterestListIds] = useState<string[]>([]);
  const [textFilter, setTextFilter] = useState<string>("");
  const [filterState, setFilterState] = useState<FilterRecordType>({});
  const [searchFlag, setSearchFlag] = useState(false);
  const [pageState, setPageState] = useState<PageState>({ page: 0, nbPages: 0, pageSize: 25 });
  const setSelectedPage = (page: number) => setPageState((prevState) => ({ ...prevState, page }));
  const setNbPages = (nbPages: number) => setPageState((prevState) => ({ ...prevState, nbPages }));
  const [searchState, setSearchState] = useState<SearchState>({ nbHits: 0, hits: [], page: 0 });


  const addFilter = (filterName: string, value: FilterSetting, filterType = "text" as FilterType) => {
    const valueTransformers = {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      array: (_prevState: FilterRecordType) => [...value as string[]] as ArrayFilterSetting,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      range: (_prevState: FilterRecordType) => (value as RangeFilterSetting),
      rangeMax: (prevState: FilterRecordType) => ({
        ...(prevState[filterName]?.value || {}) as RangeFilterSetting,
        max: value,
      }) as RangeFilterSetting,
      rangeMin: (prevState: FilterRecordType) => ({
        ...(prevState[filterName]?.value || {}) as RangeFilterSetting,
        min: value,
      } as RangeFilterSetting),
    } as Record<FilterType, (prevState: FilterRecordType) => FilterSetting>;
    const defaultTransform = () => `${value}`;
    const getNewValue = (prevState: FilterRecordType) =>
      (valueTransformers[filterType] || defaultTransform)(prevState);
    const getFilterType = () => {
      if (filterType === "rangeMin" || filterType === "rangeMax") {
        return "range";
      }
      if ((value === true || value === false) && filterType !== "inverseBoolean") {
        return "boolean";
      }
      return filterType;
    };
    setFilterState((prevState) => ({
      ...prevState,
      [filterName]: {
        type: getFilterType(),
        value: getNewValue(prevState),
      } as FilterData,
    }));
  };

  const removeFilter = (filterName: string, filterType?: FilterType) =>
    setFilterState((prevState) => {
      const newState = { ...prevState };
      if (["rangeMin", "rangeMax"].includes(filterType || "")) {
        const filterData = newState[filterName];
        if (!filterData) {
          return newState;
        }
        if (filterType === "rangeMin") {
          delete filterData.min;
        } else {
          delete filterData.max;
        }
        return { ...newState, [filterName]: filterData };
      }
      delete newState[filterName];
      return newState;
    });
  const clearFilters = () => setFilterState({});


  return {
    interestListIds,
    setInterestListIds,
    textFilter,
    searchFlag,
    setSearchFlag,
    setTextFilter,
    addFilter,
    removeFilter,
    clearFilters,
    filterState,
    setFilterState,
    search: () => setSearchFlag(true),
    searchState,
    setSearchState,
    pageState,
    setSelectedPage,
    setNbPages,
  };
};

export const useSidebarFilters = (): FilterManager => {
  const context = React.useContext(SidebarFiltersContext);
  if (typeof context === "undefined") {
    throw new Error("useSidebarFilters must be used within a SidebarFiltersProvider");
  }
  if (context === null) {
    throw new Error("useSidebarFilters is not initialized correctly");
  }
  return context;
};

export const SidebarFiltersProvider: React.FC = (props) => {
  const value = defineFilters();

  return (
    <SidebarFiltersContext.Provider value={value}>
      {props.children}
    </SidebarFiltersContext.Provider>
  );
};
